import { useMemo } from 'react';
import type { ReportRangeArgs } from '@fluxter/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface ReportRangePickerProps {
  value: ReportRangeArgs;
  onChange: (next: ReportRangeArgs) => void;
}

type Preset = 'this-month' | 'last-month' | 'last-30' | 'this-year' | 'all-time' | 'custom';

const PRESET_LABEL: Record<Preset, string> = {
  'this-month': 'This month',
  'last-month': 'Last month',
  'last-30': 'Last 30 days',
  'this-year': 'This year',
  'all-time': 'All time',
  custom: 'Custom range',
};

const toDateOnly = (d: Date): string => d.toISOString().slice(0, 10);

const computePreset = (preset: Preset): ReportRangeArgs => {
  const now = new Date();
  switch (preset) {
    case 'this-month': {
      const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
      return { from: toDateOnly(from), to: toDateOnly(to) };
    }
    case 'last-month': {
      const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return { from: toDateOnly(from), to: toDateOnly(to) };
    }
    case 'last-30': {
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      // half-open upper bound = tomorrow's date so today's entries are included
      const to = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      return { from: toDateOnly(from), to: toDateOnly(to) };
    }
    case 'this-year': {
      const from = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      const to = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1));
      return { from: toDateOnly(from), to: toDateOnly(to) };
    }
    case 'all-time':
      return {};
    case 'custom':
      return {};
  }
};

const detectPreset = (value: ReportRangeArgs): Preset => {
  if (!value.from && !value.to) return 'all-time';
  const presets: Preset[] = ['this-month', 'last-month', 'last-30', 'this-year'];
  for (const p of presets) {
    const candidate = computePreset(p);
    if (candidate.from === value.from && candidate.to === value.to) return p;
  }
  return 'custom';
};

export const ReportRangePicker = ({ value, onChange }: ReportRangePickerProps) => {
  const currentPreset = useMemo(() => detectPreset(value), [value]);

  const handlePreset = (next: string) => {
    if (next === 'custom') return; // keep current dates; user edits manually
    onChange(computePreset(next as Preset));
  };

  return (
    <div className="bg-muted/30 flex flex-wrap items-start gap-3 rounded-md border p-3">
      <div className="space-y-1">
        <Label className="text-muted-foreground text-xs">Range</Label>
        <Select value={currentPreset} onValueChange={handlePreset}>
          <SelectTrigger size="sm" className="min-w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
              <SelectItem key={p} value={p}>
                {PRESET_LABEL[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="report-from" className="text-muted-foreground text-xs">
          From
        </Label>
        <Input
          id="report-from"
          type="date"
          value={value.from?.slice(0, 10) ?? ''}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ ...value, from: next === '' ? undefined : next });
          }}
          className="w-40"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="report-to" className="text-muted-foreground text-xs">
          To
        </Label>
        <Input
          id="report-to"
          type="date"
          value={value.to?.slice(0, 10) ?? ''}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ ...value, to: next === '' ? undefined : next });
          }}
          className="w-40"
        />
        <p className="text-muted-foreground/80 text-xs">Excludes this date.</p>
      </div>
    </div>
  );
};
